import { EnhancedBaseService } from '../base/EnhancedBaseService';
import type { ServiceResponse } from '../base/ServiceTypes';

interface UserConnection {
  id?: string;
  user_id: string;
  platform: string;
  platform_user_id?: string;
  platform_username?: string;
  metadata?: any;
  is_active: boolean;
  last_sync?: string;
  created_at?: string;
  updated_at?: string;
}

class UserConnectionService extends EnhancedBaseService<UserConnection> {
  protected resourceName = 'user_connections';

  /**
   * Get user connections for a specific platform
   */
  async getUserConnectionsByPlatform(platform: string): Promise<ServiceResponse<UserConnection[]>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();
      const { data, error } = await this.client
        .from(this.resourceName)
        .select('*')
        .eq('user_id', ownerId)
        .eq('platform', platform)
        .eq('is_active', true);

      if (error) throw error;
      return data || [];
    });
  }

  /**
   * Get active user connections
   */
  async getActiveConnections(): Promise<ServiceResponse<UserConnection[]>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();
      const { data, error } = await this.client
        .from(this.resourceName)
        .select('*')
        .eq('user_id', ownerId)
        .eq('is_active', true);

      if (error) throw error;
      return data || [];
    });
  }

  /**
   * Get Shopify connection for inferring industry
   */
  async getShopifyConnection(): Promise<ServiceResponse<UserConnection | null>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();
      const { data, error } = await this.client
        .from(this.resourceName)
        .select('*')
        .eq('user_id', ownerId)
        .eq('platform', 'shopify')
        .eq('is_active', true)
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 is "no rows returned"
      return data || null;
    });
  }
}

export const userConnectionService = new UserConnectionService();
